import {
  type ReactElement,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import clsx from "clsx";
import { formatDuration, intervalToDuration } from "date-fns";
import { capitalize, compact, orderBy } from "lodash";
import moment from "moment";
import { Link } from "react-router-dom";

import amazonFilledIcon from "@assets/platform-icons/amazon-filled-icon.svg";
import applovinFilledIcon from "@assets/platform-icons/applovin-filled-icon.svg";
import blisspointFilledIcon from "@assets/platform-icons/blisspoint-filled-icon.svg";
import facebookFilledIcon from "@assets/platform-icons/facebook-filled-icon.svg";
import genericFilledIcon from "@assets/platform-icons/generic-filled-icon.svg";
import googleFilledIcon from "@assets/platform-icons/google-filled-icon.svg";
import mediamathFilledIcon from "@assets/platform-icons/mediamath-filled-icon.svg";
import microsoftFilledIcon from "@assets/platform-icons/microsoft-filled-icon.svg";
import mntnFilledIcon from "@assets/platform-icons/mntn-filled-icon.svg";
import northbeamFilledIcon from "@assets/platform-icons/northbeam-filled-icon.svg";
import pinterestFilledIcon from "@assets/platform-icons/pinterest-filled-icon.svg";
import s2sFilledIcon from "@assets/platform-icons/s2s-filled-icon.svg";
import shopifyFilledIcon from "@assets/platform-icons/shopify-filled-icon.svg";
import snapchatFilledIcon from "@assets/platform-icons/snapchat-filled-icon.svg";
import tiktokFilledIcon from "@assets/platform-icons/tiktok-filled-icon.svg";
import veFilledIcon from "@assets/platform-icons/ve-filled-icon.svg";
import { useUser } from "@components/user-context";
import { useNorthbeamQuery } from "@hooks/http";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { type DataRefreshTime } from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { DATA_REFRESH_TIME_QUERY } from "@north-beam/nb-client-side/queries";

import { GenericTooltip } from "../tooltip-container";

const KEY_ICON_MAP: { [key: string]: string } = {
  adwords: googleFilledIcon,
  microsoft: microsoftFilledIcon,
  fb: facebookFilledIcon,
  snapchat: snapchatFilledIcon,
  pinterest: pinterestFilledIcon,
  tiktok: tiktokFilledIcon,
  ve: veFilledIcon,
  dv360_ve: veFilledIcon,
  bliss_point_media: blisspointFilledIcon,
  bliss_point_media_linear: blisspointFilledIcon,
  bliss_point_media_audio: blisspointFilledIcon,
  amazon_marketplace_aggregated: amazonFilledIcon,
  applovin: applovinFilledIcon,
  mediamath: mediamathFilledIcon,
  spend_impressions__mntn: mntnFilledIcon,
  "northbeam/pixel": northbeamFilledIcon,
  "northbeam/custom-orders": northbeamFilledIcon,
  "northbeam/custom-spend": northbeamFilledIcon,
  "northbeam/enquire-labs": northbeamFilledIcon,
  "northbeam/influencer-spend": northbeamFilledIcon,
  shopify: shopifyFilledIcon,

  orders_custom: s2sFilledIcon,
  orders_dummy: genericFilledIcon,
  amazon: amazonFilledIcon,
  amazon_ads: amazonFilledIcon,
};

const KEY_CONNECTION_MAP: { [key: string]: string[] } = {
  adwords: ["integration_google_adwords"],
  microsoft: ["integration_microsoft"],
  fb: ["integration_facebook_ads"],
  snapchat: ["integration_snapchat"],
  pinterest: ["integration_pinterest"],
  tiktok: ["integration_tiktok"],
  "bliss-point-media": ["bliss-point-media"],
  amazon: ["integration_amazon_seller"],
  "northbeam/pixel": ["Northbeam pixel"],
  shopify: ["integration_shopify"],
  amazon_ads: ["integration_amazon"],
};

const KEY_TITLE_MAP = {
  // REVENUE
  "northbeam/pixel": "Northbeam Pixel",
  shopify: "Shopify",
  orders_dummy: "Dummy",
  amazon: "Amazon",

  // SPEND
  adwords: "Google Ads",
  microsoft: "Microsoft Ads",
  fb: "Facebook Ads",
  snapchat: "Snapchat Ads",
  pinterest: "Pinterest Ads",
  tiktok: "TikTok Ads",
  ve: "VE",
  dv360_ve: "DV360 VE",
  "bliss-point-media": "Bliss Point Media",
  amazon_ads: "Amazon Marketplace Aggregated",

  mediamath: "Mediamath",
  mntn: "MNTN",
  criteo: "Criteo",

  tatari: "Tatari",
  adelphic: "Adelphic",
  adnet: "Adnet",
  "attentive-mobile": "Attentive Mobile",
  hivewyre: "Hivewyre",
  klaviyo: "Klaviyo",
  applovin: "AppLovin",

  "northbeam/custom-orders": "Orders API (S2S, WooCommerce and Magento)",
  "northbeam/custom-spend": "Northbeam Spend Sheet/API",
  "northbeam/enquire-labs": "Northbeam Enquire Labs",
  "northbeam/influencer-spend": "Northbeam Influencers",

  tradedesk: "Tradedesk",
  zeta: "Zeta",

  "northbeam/partnership-instagram": "Partnership Instagram",
  "northbeam/partnership-podcast": "Partnership Podcast",
  "northbeam/partnership-youtube": "Partnership Youtube",
  "northbeam/partnership-tiktok": "Partnership TikTok",
};

function fromThenToNow(from: Date) {
  const duration = intervalToDuration({
    start: from,
    end: new Date(),
  });

  const formattedDuration = formatDuration(duration, {
    format: ["days", "hours", "minutes"],
    delimiter: " and ",
  }).replace("minutes", "mins");

  if (!formattedDuration) {
    return "Now";
  }

  return `${formattedDuration} ago`;
}

type HumanReadableBlessings = {
  key: string;
  keyTitleMap: Record<string, string>;
  displayName: string;
  data: {
    title: string;
    time: string;
    needsReconnect: boolean;
    relativeLastUpdate: string;
    icon: ReactElement;
    iconString: string;
  }[];
}[];

function toHumanReadable(s: string) {
  // Replace underscores with spaces and capitalize each word
  return s
    .split(/,|-|_|\//)
    .map(capitalize)
    .join(" ");
}
function humanReadablePlatform(platform: string) {
  return toHumanReadable(platform);
}

const ONE_MIN_IN_MILLISECONDS = 60_000;

export const TopBarDataFreshnessTooltip = ({
  brokenConnections,
}: {
  brokenConnections: string[];
}) => {
  const { data, startPolling, stopPolling } =
    useNorthbeamQuery<DataRefreshTime>(DATA_REFRESH_TIME_QUERY, {
      doNotRaiseErrorOnFailure: true,
      fetchPolicy: "cache-and-network",
    });

  useEffect(() => {
    startPolling(ONE_MIN_IN_MILLISECONDS);
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  const {
    dataBlessingTimes,
    dataProcessedAt,
    nextProcessedAt,
    processingPerDay,
    timezone,
  } = useMemo(() => {
    if (data) {
      return {
        ...data.me,
        dataBlessingTimes: data.me.dataBlessingTimesV2,
      };
    }
    return {
      dataBlessingTimes: [],
      dataProcessedAt: "",
      nextProcessedAt: "",
      processingPerDay: 0,
      timezone: "",
    };
  }, [data]);

  const { designElementManager } = useDesignElementManager();

  const [autoUpdatedDataBlessingData, setAutoUpdatingBlessingData] =
    useState<HumanReadableBlessings>([]);
  const [
    autoUpdatedDataProcessedAtReadable,
    setAutoUpdatedDataProcessedAtReadable,
  ] = useState<{
    key: string;
    humanReadable: string;
  }>();
  const [
    autoUpdatedNextProcessedAtReadable,
    setAutoUpdatedNextProcessedAtReadable,
  ] = useState<{
    key: string;
    humanReadable: string;
  }>();

  const createDataProcessedAtReadable = useCallback(() => {
    const time = moment(dataProcessedAt).tz(timezone);

    const humanReadable = time.isValid()
      ? fromThenToNow(time.toDate())
      : "Unknown";

    return {
      key: "Last Processed",
      humanReadable,
    };
  }, [dataProcessedAt, timezone]);

  const createNextProcessedAtReadable = useCallback(() => {
    const key = "Next Processed";

    if (!nextProcessedAt) {
      return {
        key,
        humanReadable: "Unknown",
      };
    }
    const time = moment(nextProcessedAt).tz(timezone);

    // if the time is less than now
    if (time.toDate().getTime() - new Date().getTime() < 0) {
      return {
        key,
        humanReadable: "in a few minutes",
      };
    }

    const humanReadable = time.isValid()
      ? `in ${fromThenToNow(time.toDate()).replace("ago", "")}`
      : "Unknown";

    return {
      key,
      humanReadable: humanReadable.includes("Now") ? "Now" : humanReadable,
    };
  }, [nextProcessedAt, timezone]);

  const createDataBlessingDataReadable = useCallback(() => {
    const dataBlessingsOrderedByTimestamp = orderBy(
      dataBlessingTimes,
      "timestamp",
    );
    const humanReadableDataBlessing: HumanReadableBlessings = [
      { key: "spend", displayName: "Spend", keyTitleMap: KEY_TITLE_MAP },
      {
        key: "revenue",
        displayName: "Revenue",
        keyTitleMap: KEY_TITLE_MAP,
      },
    ].map(({ key, displayName, keyTitleMap }) => {
      const filteredByCategory = dataBlessingsOrderedByTimestamp.filter(
        (i) => i.type === key,
      );
      const data: HumanReadableBlessings[number]["data"] = compact(
        filteredByCategory.map((blessing) => {
          let maxTime = moment("INVALID");
          const iconString = KEY_ICON_MAP[blessing.platform];
          const icon: ReactElement = designElementManager
            .getDesignElement(blessing.platform.split("/")?.[0] ?? "")
            .getComponent({
              size: "25px",
            });

          const dataBlessingTime = moment(blessing.timestamp);
          const time = dataBlessingTime
            .tz(timezone)
            .format("MMM D [@] HH:mm z");
          const relativeLastUpdate = fromThenToNow(dataBlessingTime.toDate());
          const connectionKey: string[] = KEY_CONNECTION_MAP[blessing.platform];
          const brokenConnection = connectionKey?.filter((connection) =>
            brokenConnections.includes(connection),
          );

          const needsReconnect =
            brokenConnection && brokenConnection.length > 0;

          const title: string =
            keyTitleMap[blessing.platform as keyof typeof keyTitleMap] ||
            humanReadablePlatform(blessing.platform);

          if (dataBlessingTime.isValid()) {
            if (!maxTime.isValid() || maxTime < dataBlessingTime) {
              maxTime = dataBlessingTime;
            }
            return {
              title,
              time,
              relativeLastUpdate,
              needsReconnect,
              icon,
              iconString,
            };
          } else {
            return {
              title,
              time: "",
              relativeLastUpdate: "",
              needsReconnect,
              icon,
              iconString,
            };
          }
        }),
      );
      return {
        key,
        keyTitleMap,
        displayName,
        data,
      };
    });

    return humanReadableDataBlessing;
  }, [brokenConnections, dataBlessingTimes, designElementManager, timezone]);

  useEffect(() => {
    setAutoUpdatedDataProcessedAtReadable(createDataProcessedAtReadable());
    const interval = setInterval(() => {
      setAutoUpdatedDataProcessedAtReadable(createDataProcessedAtReadable());
    }, ONE_MIN_IN_MILLISECONDS);

    return () => clearInterval(interval);
  }, [createDataProcessedAtReadable]);

  useEffect(() => {
    setAutoUpdatingBlessingData(createDataBlessingDataReadable());
    const interval = setInterval(() => {
      setAutoUpdatingBlessingData(createDataBlessingDataReadable());
    }, ONE_MIN_IN_MILLISECONDS);

    return () => clearInterval(interval);
  }, [createDataBlessingDataReadable]);

  useEffect(() => {
    setAutoUpdatedNextProcessedAtReadable(createNextProcessedAtReadable());
    const interval = setInterval(() => {
      setAutoUpdatedNextProcessedAtReadable(createNextProcessedAtReadable());
    }, ONE_MIN_IN_MILLISECONDS);

    return () => clearInterval(interval);
  }, [createNextProcessedAtReadable]);

  return (
    <>
      <GenericTooltip
        overlayClassName="w-[42rem]"
        content={
          <div>
            {autoUpdatedDataBlessingData.map((obj) => {
              return <DataBlessingMapping key={obj.key} obj={obj} />;
            })}
            <div className="mx-2 mt-1">
              <div>
                <span className="italic font-semibold text-interstellar">
                  Last Processed
                </span>
                <span className="ml-1 text-interstellar-1">
                  is when your data last completed processing and became ready
                  for use in the dashboard
                </span>
              </div>
              <div className="mt-2">
                <span className="italic font-semibold text-interstellar">
                  Last Retrieved
                </span>
                <span className="ml-1 text-interstellar-1">
                  is when Northbeam last pulled data from the platform
                </span>
              </div>
            </div>
          </div>
        }
        noInfoCircle
        captureEventString="HoverOverUpdatedAt"
      >
        <div
          className={clsx(
            "flex flex-col space-y-0.5 cursor-pointer",
            "hover:bg-cosmic-4 transform transition-colors ease-in rounded-lg py-1 px-3",
          )}
        >
          <span className="font-semibold text-interstellar-1 text-xxxs">
            LAST PROCESSED
          </span>
          <DataBlessing obj={autoUpdatedDataProcessedAtReadable} />
        </div>
      </GenericTooltip>
      <GenericTooltip
        overlayClassName="w-[20rem]"
        content={
          <div>
            Time estimate for which your data will next finish processing and be
            ready for use in the dashboard
            {processingPerDay < 24 && (
              <div className="mt-1">
                If you are interested in faster processing,{" "}
                <a
                  href="mailto:sales@northbeam.io"
                  target="_blank"
                  rel="noreferrer"
                >
                  reach out here
                </a>
              </div>
            )}
          </div>
        }
        noInfoCircle
        captureEventString="HoverOverUpdatedAt"
      >
        <div
          className={clsx(
            "flex flex-col space-y-0.5 cursor-pointer",
            "hover:bg-cosmic-4 transform transition-colors ease-in rounded-lg py-1 px-3",
          )}
        >
          <span className="font-semibold text-interstellar-1 text-xxxs">
            EST. NEXT PROCESSING COMPLETE
          </span>
          <DataBlessing obj={autoUpdatedNextProcessedAtReadable} />
        </div>
      </GenericTooltip>
    </>
  );
};

function DataBlessingMapping({ obj }: { obj: HumanReadableBlessings[number] }) {
  const { user } = useUser();

  if (obj.data.length === 0) {
    return null;
  }

  return (
    <div className="p-2 text-xs whitespace-nowrap min-w-fit" key={obj.key}>
      <div className="text-xl font-bold text-interstellar">
        {obj.displayName}
      </div>
      <div
        className={clsx("grid grid-cols-[45%_60%] mt-2 gap-2 text-sm", {
          "w-[38rem]": obj.data.length >= 6,
        })}
      >
        <div>Platform</div>
        <div>Last Retrieved</div>
      </div>
      <div className="max-h-[15rem] overflow-y-auto overflow-x-hidden">
        {obj.data.map((item) => (
          <div
            key={item?.title}
            className={clsx(
              "border-b border-gray-3 py-2 gap-2",
              "grid grid-cols-[45%_60%] items-center",
            )}
          >
            <div
              className="flex items-center space-x-2 text-interstellar-1"
              title={item?.title}
            >
              {item.iconString ? (
                <img src={item?.iconString} alt={`${item?.title} logo`} />
              ) : (
                item.icon
              )}
              <span className={clsx("truncate text-ellipsis")}>
                {item?.title}
              </span>
            </div>
            <div className="text-gray+1">
              {item?.needsReconnect ? (
                <div className="grid grid-cols-[50%_5%_45%]">
                  <span className="text-bad">
                    <i
                      className={clsx(
                        "fa-sharp fa-regular fa-sharp",
                        "fa-circle-exclamation",
                      )}
                    />{" "}
                    {item?.relativeLastUpdate}
                  </span>
                  <div />
                  <Link
                    to={user.pathFromRoot("settings/connections")}
                    className="ml-2 text-cosmic hover-underline"
                  >
                    Reconnect
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-[50%_5%_45%] place-content-center">
                  <span className="font-medium text-good">
                    {item?.relativeLastUpdate}
                  </span>
                  <span>•</span>
                  <span className="text-gray+1">{item?.time}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DataBlessing({
  obj,
}: {
  obj: { key: string; humanReadable: string } | undefined;
}) {
  return (
    <div className="text-xxs leading-3 tracking-[-0.3px]" key={obj?.key}>
      <span className="text-cosmic">{obj?.humanReadable}</span>
    </div>
  );
}
